import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect
} from "react";
import ReactDOM from "react-dom";
import {
  Container,
  Row,
  Col,
  Form,
  InputGroup,
  Button,
  Spinner,
  ListGroup,
  Card
} from "react-bootstrap";

import { autocompleteSearch } from "./api";

const AsplHeroSection = ({ executeSearch }) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const wrapRef = useRef(null);
  const inputWrapRef = useRef(null);
  const abortRef = useRef(null);

  const [anchorRect, setAnchorRect] = useState(null);

  /* Measure dropdown anchor */
  const measure = useCallback(() => {
    if (!inputWrapRef.current) return;
    const rect = inputWrapRef.current.getBoundingClientRect();
    setAnchorRect({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width
    });
  }, []);

  useLayoutEffect(() => {
    if (!showDropdown) return;

    measure();
    const onScroll = () => measure();
    const onResize = () => measure();

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [showDropdown, measure]);

  /* close dropdown only when clicking outside BOTH input + dropdown */
  useEffect(() => {
    const handler = (e) => {
      if (
        wrapRef.current &&
        !wrapRef.current.contains(e.target) &&
        !document.getElementById("aspl-portal-dropdown")?.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* autocomplete */
  const fetchSuggestions = useCallback(async (value) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setShowDropdown(true);
    try {
      const res = await autocompleteSearch(value);
      const liveItems =
        res.data?.details?.getProductsInfoResponse?.productInfoList?.productInfo || [];

      const mapped = liveItems.map((item) => ({
        id: item.id || "",
        reference: item.reference || item.id || "",
        name: item.name || "",
        brand: item.producer || "",
        category: item.groupName || "",
        photo: item.photo || (Array.isArray(item.photos) ? item.photos[0] : null) || null,
      }));

      setSuggestions(mapped.slice(0, 5));
      setShowDropdown(true);
    } catch (err) {
      if (err?.name !== "AbortError") console.error(err);
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setLoading(false);
    }
  }, []);

  /* debounce typed value */
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const t = setTimeout(() => fetchSuggestions(query), 250);
    return () => clearTimeout(t);
  }, [query, fetchSuggestions]);

  /* submit handler */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setShowDropdown(false);
    executeSearch(query.trim());
  };

  /* helper for selecting suggestion */
  const handleSelect = (value) => {
    setQuery(value); // fill input correctly
    setShowDropdown(false);

    // run search after state updates
    Promise.resolve().then(() => executeSearch(value));
  };

  return (
    <section
      className="py-5"
      style={{
        position: "relative",
        minHeight: "78vh",
        display: "flex",
        alignItems: "center",
        overflow: "visible"
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(var(--bs-primary-rgb), .94) 0%, rgba(var(--bs-primary-rgb), .9) 55%, rgba(var(--bs-primary-rgb), .88) 100%)",
          backdropFilter: "blur(3px)"
        }}
      />

      <Container style={{ position: "relative" }}>
        <Row className="justify-content-center text-center">
          <Col lg={9} xl={8}>
            <h1 className="display-6 fw-bold text-white mb-2">
              Find Authentic ASPL products – Alternators, Starters, and Premium OEM Parts
            </h1>

            <p className="text-white-50 mb-4">
              Search by OEM, Reference, TecDoc or Brand
            </p>

            <div
              ref={wrapRef}
              className="position-relative mx-auto"
              style={{ maxWidth: 720 }}
            >
              <Form onSubmit={handleSubmit}>
                <div ref={inputWrapRef}>
                  <InputGroup
                    className="shadow-sm rounded-4 overflow-hidden"
                    style={{
                      background: "#ffffff",
                      border: "1px solid rgba(var(--bs-primary-rgb), .28)",
                      boxShadow: "0 10px 24px rgba(0,0,0,.14)"
                    }}
                  >
                    <Form.Control
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onFocus={() => suggestions.length && setShowDropdown(true)}
                      placeholder="Search ASPL products..."
                      className="py-3 border-0"
                      autoComplete="off"
                    />

                    <Button type="submit" variant="primary" className="px-4 fw-semibold">
                      Search
                    </Button>
                  </InputGroup>
                </div>
              </Form>

              {loading && query.length >= 2 && (
                <div
                  className="position-absolute"
                  style={{
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)"
                  }}
                >
                  <Spinner animation="border" size="sm" />
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      {showDropdown &&
        anchorRect &&
        ReactDOM.createPortal(
          <div
            id="aspl-portal-dropdown"
            style={{
              position: "absolute",
              top: anchorRect.top + 8,
              left: anchorRect.left,
              width: anchorRect.width,
              zIndex: 999999
            }}
          >
            <Card className="shadow-sm border-0 rounded-4">
              <ListGroup variant="flush">
                {loading && (
                  <ListGroup.Item className="text-center text-muted py-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Searching “{query}”...
                  </ListGroup.Item>
                )}

                {!loading && suggestions.map((item, i) => (
                  <ListGroup.Item
                    key={i}
                    action
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSelect(item.reference)}
                    className="py-2"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="bg-light border rounded"
                        style={{ width: 42, height: 42, overflow: "hidden", flexShrink: 0 }}
                      >
                        {item.photo ? (
                          <img
                            src={item.photo}
                            alt={item.reference}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : null}
                      </div>
                      <div className="min-w-0">
                        <div className="fw-semibold text-truncate">
                          {item.reference} — {item.name}
                        </div>
                        <div className="text-muted small text-truncate">
                          {item.brand} · {item.category}
                        </div>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}

                {!loading && suggestions.length === 0 && query.length >= 2 && (
                  <ListGroup.Item className="text-center text-muted py-3">
                    No quick matches yet.
                  </ListGroup.Item>
                )}

                <ListGroup.Item
                  action
                  className="text-center fw-semibold"
                  onClick={() => handleSelect(query)}
                >
                  View all results for “{query}”
                </ListGroup.Item>
              </ListGroup>
            </Card>
          </div>,
          document.body
        )}
    </section>
  );
};

export default AsplHeroSection;
